import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import {
  applyDiscountPct,
  d,
  money,
  moneyDisplay,
  qtyDisplay,
} from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ProgressBar } from "@/components/ui/progress";
import { Select } from "@/components/ui/select";
import { EmptyState } from "@/modules/shell/EmptyState";
import { cn } from "@/lib/utils";
import { PosFloorSheet } from "../components/PosFloorSheet";
import { PosTicketOptionsFields } from "../components/PosTicketOptionsFields";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { rotateDeviceInstallId } from "../sync/device-install-id";
import {
  bindPosTerminal,
  closePosDrawer,
  getPosTicket,
  listParkedPosTickets,
  listTerminalTickets,
  openPosDrawer,
  parkPosTicket,
  recallParkedPosTicket,
  sellPosTicket,
  voidPosTicket,
  type ParkedTicketSummary,
  type PosCatalogItem,
  type PosPayMethod,
  type PosSession,
} from "@/shared/api/pos";
import { loginWithPin, logout } from "@/shared/api/auth";
import {
  approveOverrideWithPin,
  createOverrideRequest,
  fetchOverrideRequest,
  redeemOverrideOtp,
} from "@/shared/api/overrides";
import { apiErrorCode, isApiError } from "@/shared/api/api-error";
import { overrideWaitMessage } from "../override-wait-math";
function cashierKey(companySlug: string, terminalId: string) {
  return `ix_pos_cashier:${companySlug}:${terminalId}`;
}
function cashierUserIdKey(companySlug: string, terminalId: string) {
  return `ix_pos_cashier_uid:${companySlug}:${terminalId}`;
}
import {
  listCachedCashiers,
  listConflictOutbox,
  loadCachedCashier,
  loadCatalogCache,
  pendingCount,
  saveCachedCashier,
  saveCatalogCache,
  saveSessionCache,
} from "../sync/idb";
import {
  cachedUserCanApprove,
  cachedUserCanSelfAllow,
  deriveCanApproveKeys,
  offlineSoftLimitBlockedMessage,
  offlineSoftLimitPath,
  type OfflinePinApprovePayload,
} from "../sync/offline-override-math";
import {
  applyLocalStockDelta,
  cacheTicketForReturn,
  enqueueMutation,
  enqueueOfflineReturn,
  hydratePos,
  resolveConflictConfirm,
  resolveConflictDrop,
  reverseLocalStockDelta,
  runSyncPass,
  voidLocalOrEnqueue,
} from "../sync/sync-engine";
import {
  advanceNumberRangePast,
  mintTerminalTicketNumber,
} from "../sync/number-range-math";
import {
  makePinVerifier,
  newSaltHex,
  normalizeUserCode,
  verifyPinAgainstCached,
} from "../sync/pin-cache-math";
import {
  isLikelyNetworkError,
  isSoftConfirmableCode,
  softSyncWarns,
  syncChipState,
  type OutboxItem,
} from "../sync/sync-math";
import { findCatalogByScan } from "../catalog-lookup-math";
import {
  addOrBumpCartLine,
  cartDraftStorageKey,
  clearCartDraft,
  loadCartDraft,
  saveCartDraft,
  type CartDraftLine,
} from "../cart-draft-math";
import {
  favoritesStorageKey,
  loadIdList,
  recentStorageKey,
  saveIdList,
} from "../catalog-prefs";
import { buildParkConflictOptions, cartItemCount } from "../park-math";
import {
  loadLocalParked,
  localParkedStorageKey,
  saveLocalParked,
  type LocalParkedTicket,
} from "../park-local";
import {
  payMethodUsesCashBills,
  suggestedTenderFills,
} from "../tender-quick-math";
import {
  filterCatalogBrowse,
  orderedRailIds,
  pickCatalogByIds,
  POS_COMPACT_MQ,
  pushRecentId,
  toggleFavoriteId,
} from "../catalog-rail-math";
import { PosCatalogBrowseSheet } from "../components/PosCatalogBrowseSheet";
import { PosProductTile } from "../components/PosProductTile";
import { PosTenderKeypad } from "../components/PosTenderKeypad";
import { PosReceipt, printPosReceipt } from "../receipt/PosReceipt";
import {
  buildReceipt,
  provisionalTicketNumber,
  type PosReceiptData,
} from "../receipt/receipt-math";

type CartLine = CartDraftLine;

function newClientId(): string {
  return crypto.randomUUID();
}

function sortCatalog(items: PosCatalogItem[]): PosCatalogItem[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name));
}

export function PosRegisterPage() {
  const { companySlug, terminalId } = useParams();
  const navigate = useNavigate();
  const confirmUi = useConfirm();
  const [session, setSession] = useState<PosSession | null>(null);
  const [catalog, setCatalog] = useState<PosCatalogItem[]>([]);
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const [parkedOpen, setParkedOpen] = useState(false);
  const [parkedTickets, setParkedTickets] = useState<ParkedTicketSummary[]>(
    [],
  );
  const [parkConflict, setParkConflict] = useState<{
    options: ReturnType<typeof buildParkConflictOptions>;
    incomingId: string;
    incomingLocal: boolean;
  } | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [tenderPadOpen, setTenderPadOpen] = useState(false);
  const [compactCatalog, setCompactCatalog] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(POS_COMPACT_MQ).matches
      : true,
  );
  const [cart, setCart] = useState<CartLine[]>([]);
  const [scan, setScan] = useState("");
  const scanInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  /** Bind conflict — offer Use this device (force rebind) — plan/28 */
  const [needsRebind, setNeedsRebind] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lastSale, setLastSale] = useState<string | null>(null);
  const [tenderAmount, setTenderAmount] = useState("");
  const [discountPct, setDiscountPct] = useState("");
  const [taxPriceMode, setTaxPriceMode] = useState<
    "" | "inclusive" | "exclusive"
  >("");
  const [taxRatePct, setTaxRatePct] = useState("");
  const [ticketNote, setTicketNote] = useState("");
  const [overrideRequestId, setOverrideRequestId] = useState<string | null>(
    null,
  );
  const [otpCode, setOtpCode] = useState("");
  const [approverCode, setApproverCode] = useState("");
  const [approverPin, setApproverPin] = useState("");
  const [offlinePinApprove, setOfflinePinApprove] =
    useState<OfflinePinApprovePayload | null>(null);
  /** Which override CTA to show after a coded soft-limit fail */
  const [softLimitNeed, setSoftLimitNeed] = useState<null | "discount">(null);
  const [payMethodId, setPayMethodId] = useState<string | null>(null);
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [catalogUpdated, setCatalogUpdated] = useState<string | null>(null);
  const [pending, setPending] = useState(0);
  const [receipt, setReceipt] = useState<PosReceiptData | null>(null);
  const [recentTickets, setRecentTickets] = useState<
    Array<{
      id: string;
      number: string;
      lifecycle: string;
      grossTotal: string;
    }>
  >([]);
  const [voidOverrideId, setVoidOverrideId] = useState<string | null>(null);
  const [cashierName, setCashierName] = useState<string | null>(null);
  const [cashierUserId, setCashierUserId] = useState<string | null>(null);
  const [pinUserCode, setPinUserCode] = useState("");
  const [pinValue, setPinValue] = useState("");
  const [openingFloat, setOpeningFloat] = useState("0");
  const [conflicts, setConflicts] = useState<OutboxItem[]>([]);
  const [conflictBusy, setConflictBusy] = useState(false);
  const [syncPassBusy, setSyncPassBusy] = useState(false);

  const chip = syncChipState({
    online,
    pendingCount: pending,
    conflictCount: conflicts.length,
  });
  const syncWarns = softSyncWarns({
    pendingCount: pending,
    catalogUpdatedAt: catalogUpdated,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(POS_COMPACT_MQ);
    function onChange() {
      setCompactCatalog(mq.matches);
    }
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!companySlug || !terminalId) return;
    setFavoriteIds(loadIdList(favoritesStorageKey(companySlug, terminalId)));
    setRecentIds(loadIdList(recentStorageKey(companySlug, terminalId)));
  }, [companySlug, terminalId]);

  const cartDraftReady = useRef(false);
  const drawerId = session?.drawer?.id ?? null;

  useEffect(() => {
    cartDraftReady.current = false;
    if (!companySlug || !terminalId || !drawerId) {
      setCart([]);
      return;
    }
    setCart(loadCartDraft(cartDraftStorageKey(companySlug, terminalId, drawerId)));
    cartDraftReady.current = true;
  }, [companySlug, terminalId, drawerId]);

  useEffect(() => {
    if (!cartDraftReady.current || !companySlug || !terminalId || !drawerId) {
      return;
    }
    saveCartDraft(
      cartDraftStorageKey(companySlug, terminalId, drawerId),
      cart,
    );
  }, [cart, companySlug, terminalId, drawerId]);

  useEffect(() => {
    if (softLimitNeed === "discount") {
      setAdvancedOpen(true);
    }
  }, [softLimitNeed]);

  useEffect(() => {
    if (!companySlug || !terminalId) return;
    setCashierName(
      sessionStorage.getItem(cashierKey(companySlug, terminalId)),
    );
    setCashierUserId(
      sessionStorage.getItem(cashierUserIdKey(companySlug, terminalId)),
    );
  }, [companySlug, terminalId]);

  function rememberCashier(label: string, userId: string) {
    if (!companySlug || !terminalId) return;
    sessionStorage.setItem(cashierKey(companySlug, terminalId), label);
    sessionStorage.setItem(cashierUserIdKey(companySlug, terminalId), userId);
    setCashierName(label);
    setCashierUserId(userId);
  }

  function clearCashier() {
    if (!companySlug || !terminalId) return;
    sessionStorage.removeItem(cashierKey(companySlug, terminalId));
    sessionStorage.removeItem(cashierUserIdKey(companySlug, terminalId));
    setCashierName(null);
    setCashierUserId(null);
  }

  async function reloadTickets() {
    if (!companySlug || !terminalId || !online) return;
    try {
      const d = await listTerminalTickets(companySlug, terminalId);
      const posted = d.tickets
        .filter((t) => t.lifecycle === "posted")
        .slice(0, 5);
      setRecentTickets(posted);
      // Cache lines so offline returns work after a sync — plan/28
      await Promise.all(
        posted.map(async (t) => {
          try {
            const detail = await getPosTicket(companySlug, t.id);
            await cacheTicketForReturn({
              ticketId: detail.ticket.id,
              terminalId,
              number: detail.ticket.number,
              warehouseId: detail.ticket.warehouseId,
              lines: detail.lines.map((l) => ({
                sourceLineId: l.id,
                productId: l.productId,
                qty: l.qty,
                lotCode: l.lotCode,
                serialCode: l.serialCode,
              })),
              savedAt: new Date().toISOString(),
            });
          } catch {
            /* ignore per ticket */
          }
        }),
      );
    } catch {
      /* offline / ignore */
    }
  }

  async function refreshPending() {
    if (!terminalId) return;
    const [p, c] = await Promise.all([
      pendingCount(terminalId),
      listConflictOutbox(terminalId),
    ]);
    setPending(p);
    setConflicts(c);
  }

  async function hydrate() {
    if (!companySlug || !terminalId) return;
    const result = await hydratePos(companySlug, terminalId, online);
    setSession(result.session);
    setCatalog(sortCatalog(result.catalog));
    setCatalogUpdated(result.catalogUpdated);
    const def =
      result.session.payMethods.find((p) => p.isDefault) ??
      result.session.payMethods[0] ??
      null;
    setPayMethodId(def?.id ?? null);
    await refreshPending();
    if (result.fromCache) {
      setStatus("Using cached register — will sync when online");
    }
    await reloadTickets();
  }

  async function tryDrain() {
    if (!companySlug || !terminalId || !navigator.onLine) return;
    setSyncPassBusy(true);
    try {
      const { drained, catalog: cat, conflict } = await runSyncPass(
        companySlug,
        terminalId,
      );
      await refreshPending();
      if (cat) {
        setCatalog(sortCatalog(cat.items));
        setCatalogUpdated(cat.watermark.lastUpdated);
      }
      if (conflict) {
        setStatus(
          conflict.lastSyncError
            ? `Sync paused — ${conflict.lastSyncError}`
            : "Sync paused — resolve below",
        );
      } else if (drained > 0) {
        setStatus(`Synced ${drained} queued sale${drained === 1 ? "" : "s"}`);
        try {
          const again = await hydratePos(companySlug, terminalId, true);
          setSession(again.session);
        } catch {
          /* keep local session */
        }
      }
    } finally {
      setSyncPassBusy(false);
    }
  }

  async function onConfirmConflict(item: OutboxItem) {
    if (!companySlug) return;
    setConflictBusy(true);
    setError(null);
    try {
      await resolveConflictConfirm(companySlug, item);
      setStatus("Queued sale synced");
      await refreshPending();
      await tryDrain();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Confirm sync failed");
      await refreshPending();
    } finally {
      setConflictBusy(false);
    }
  }

  async function onDropConflict(item: OutboxItem) {
    const ok = await confirmUi({
      title: "Drop from sync queue?",
      description:
        item.kind === "pos.sell"
          ? "Drop this unsynced sale from the queue? It will not post to the server (local stock restored)."
          : "Drop this queued action? It will not sync to the server.",
      confirmLabel: "Drop",
      destructive: true,
    });
    if (!ok) return;
    setConflictBusy(true);
    setError(null);
    try {
      const { sellLines } = await resolveConflictDrop(item);
      if (sellLines.length > 0) {
        setCatalog((prev) => sortCatalog(reverseLocalStockDelta(prev, sellLines)));
        const cached = companySlug && terminalId
          ? await loadCatalogCache(companySlug, terminalId)
          : null;
        if (cached && companySlug && terminalId) {
          await saveCatalogCache({
            ...cached,
            items: reverseLocalStockDelta(cached.items, sellLines),
          });
        }
      }
      setStatus("Dropped from sync queue");
      await refreshPending();
      await tryDrain();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Drop failed");
      await refreshPending();
    } finally {
      setConflictBusy(false);
    }
  }

  async function onUseThisDevice() {
    if (!companySlug || !terminalId) return;
    const ok = await confirmUi({
      title: "Move register to this device?",
      description:
        "The other browser or tablet will stop being the register until someone opens POS there again.",
      confirmLabel: "Use this device",
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      try {
        await bindPosTerminal(companySlug, terminalId, { force: true });
      } catch (err: unknown) {
        // Safari: corrupt localStorage install id → Headers ByteString reject.
        const msg = err instanceof Error ? err.message : "";
        if (!/did not match the expected pattern|invalid header/i.test(msg)) {
          throw err;
        }
        rotateDeviceInstallId();
        await bindPosTerminal(companySlug, terminalId, { force: true });
      }
      setNeedsRebind(false);
      await hydrate();
      setStatus("Register moved to this device");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not move register");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!companySlug || !terminalId) return;
    let cancelled = false;
    setNeedsRebind(false);
    hydrate()
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : "Failed";
        if (msg === "Not signed in") {
          navigate("/login", { replace: true });
          return;
        }
        const boundElsewhere =
          apiErrorCode(err) === "conflict" ||
          /already (bound|open) on another device/i.test(msg);
        if (boundElsewhere) {
          setNeedsRebind(true);
          setError("This register is already open on another device.");
          return;
        }
        setError(msg);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, terminalId, navigate]);

  useEffect(() => {
    function onOnline() {
      setOnline(true);
      void tryDrain();
    }
    function onOffline() {
      setOnline(false);
    }
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, terminalId]);

  useEffect(() => {
    if (!online || !companySlug) return;
    const waitingId = overrideRequestId ?? voidOverrideId;
    if (!waitingId) return;
    let cancelled = false;
    const kind = voidOverrideId && voidOverrideId === waitingId ? "void" : "sale";

    async function poll() {
      try {
        const { request } = await fetchOverrideRequest(
          companySlug!,
          waitingId!,
        );
        if (cancelled) return;
        const msg = overrideWaitMessage(request.status, kind);
        if (request.status === "pending") {
          if (msg) setStatus(msg);
          return;
        }
        if (request.status === "approved") {
          setStatus(msg);
          setError(null);
          return;
        }
        if (request.status === "denied") {
          setError(msg ?? "Override denied");
          setStatus(null);
          if (kind === "void") setVoidOverrideId(null);
          else setOverrideRequestId(null);
          return;
        }
        if (request.status === "consumed") {
          setStatus(msg);
          if (kind === "void") setVoidOverrideId(null);
          else setOverrideRequestId(null);
        }
      } catch {
        /* keep waiting — network blip */
      }
    }

    void poll();
    const id = window.setInterval(() => {
      void poll();
    }, 4_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [online, companySlug, overrideRequestId, voidOverrideId]);

  useEffect(() => {
    if (!online || !companySlug || !terminalId) return;
    const id = window.setInterval(() => {
      void tryDrain();
    }, 30_000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online, companySlug, terminalId]);

  const cartTotal = cart.reduce(
    (acc, line) => acc.add(d(line.unitPrice).mul(line.qty)),
    d(0),
  );
  const discPctNum = discountPct.trim() ? Number(discountPct) : 0;
  const dueTotal =
    Number.isFinite(discPctNum) && discPctNum > 0
      ? d(applyDiscountPct(cartTotal, discPctNum).discounted)
      : cartTotal;
  const tenderParsed = tenderAmount.trim() ? d(tenderAmount) : dueTotal;
  const changePreview = tenderParsed.sub(dueTotal);
  const showOverrideStrip = Boolean(
    softLimitNeed ||
      overrideRequestId ||
      voidOverrideId ||
      status?.includes("OTP") ||
      status?.includes("manager PIN") ||
      status?.includes("Waiting") ||
      status?.includes("Override granted") ||
      status?.includes("Void approved") ||
      status?.includes("Void override granted"),
  );

  const favoriteSet = useMemo(() => new Set(favoriteIds), [favoriteIds]);
  const cartQtyByProduct = useMemo(() => {
    const m = new Map<string, number>();
    for (const line of cart) m.set(line.productId, line.qty);
    return m;
  }, [cart]);

  const compactRailProducts = useMemo(() => {
    const q = scan.trim();
    if (q.length >= 2) {
      return filterCatalogBrowse(catalog, q).slice(0, 12);
    }
    const catalogIdSet = new Set(catalog.map((p) => p.id));
    const ids = orderedRailIds(favoriteIds, recentIds, catalogIdSet);
    return pickCatalogByIds(catalog, ids);
  }, [catalog, favoriteIds, recentIds, scan]);

  function onToggleFavorite(productId: string) {
    setFavoriteIds((prev) => {
      const next = toggleFavoriteId(prev, productId);
      if (companySlug && terminalId) {
        saveIdList(favoritesStorageKey(companySlug, terminalId), next);
      }
      return next;
    });
  }

  function addProduct(p: PosCatalogItem) {
    setCart((prev) =>
      addOrBumpCartLine(prev, {
        productId: p.id,
        name: p.name,
        unitPrice: p.price,
        qty: 1,
        trackLot: p.trackLot,
        trackSerial: p.trackSerial,
      }),
    );
    setRecentIds((prev) => {
      const next = pushRecentId(prev, p.id);
      if (companySlug && terminalId) {
        saveIdList(recentStorageKey(companySlug, terminalId), next);
      }
      return next;
    });
    setLastSale(null);
    setStatus(null);
    setError(null);
  }

  function wipeCartDraft() {
    if (!companySlug || !terminalId || !drawerId) return;
    clearCartDraft(cartDraftStorageKey(companySlug, terminalId, drawerId));
  }

  async function refreshParkedList() {
    if (!companySlug || !terminalId) return;
    const local = loadLocalParked(
      localParkedStorageKey(companySlug, terminalId),
    ).map((p) => ({
      id: p.id,
      number: p.number,
      grossTotal: p.grossTotal,
      terminalId: p.terminalId,
      terminalName: p.terminalName,
      itemCount: p.itemCount,
      createdAt: p.createdAt,
    }));
    if (!online) {
      setParkedTickets(local);
      return;
    }
    try {
      const remote = await listParkedPosTickets(companySlug);
      const remoteIds = new Set(remote.tickets.map((t) => t.id));
      setParkedTickets([
        ...remote.tickets,
        ...local.filter((l) => !remoteIds.has(l.id)),
      ]);
    } catch {
      setParkedTickets(local);
    }
  }

  function applyRecalledLines(args: {
    lines: CartLine[];
    discountPct: string;
    taxRatePct: string;
    taxPriceMode: "" | "inclusive" | "exclusive";
    note: string | null;
  }) {
    setCart(args.lines);
    setDiscountPct(args.discountPct);
    setTaxRatePct(args.taxRatePct);
    setTaxPriceMode(args.taxPriceMode);
    setTicketNote(args.note ?? "");
    setTenderAmount("");
    setLastSale(null);
    setError(null);
    setStatus("Recalled — continue ringing or Charge");
    setParkedOpen(false);
    setParkConflict(null);
    focusScanField();
  }

  async function onParkCart() {
    if (!companySlug || !terminalId || !session?.drawer) return;
    if (cart.length === 0) {
      setError("Cart is empty");
      return;
    }
    setBusy(true);
    setError(null);
    const ticketId = newClientId();
    const idem = `park:${ticketId}`;
    const due = moneyDisplay(dueTotal);
    const itemCount = cartItemCount(cart);
    try {
      // Mint from the local cursor (same as Charge) so park doesn't leave the
      // client nextSeq behind the server — charge-after-recall would 409.
      const mint = mintTerminalTicketNumber(
        session.numberRange
          ? {
              nextSeq: session.numberRange.nextSeq,
              rangeEnd: session.numberRange.rangeEnd,
              prefix: session.numberRange.prefix,
              padWidth: session.numberRange.padWidth ?? 4,
            }
          : null,
      );
      if (!mint.ok) {
        setError(
          mint.reason === "exhausted"
            ? "Number range exhausted — go online to refill"
            : "Open this register online once to load ticket numbers",
        );
        return;
      }
      const parkedNumber = mint.number;
      await persistSession({
        ...session,
        numberRange: mint.nextRange,
      });

      if (!online) {
        const local: LocalParkedTicket = {
          id: ticketId,
          number: parkedNumber,
          grossTotal: due,
          itemCount,
          terminalId,
          terminalName: session.terminal.name,
          createdAt: new Date().toISOString(),
          discountPct: discountPct.trim() || "0",
          taxRatePct:
            taxRatePct.trim() || String(session.company.taxRatePct),
          taxPriceMode:
            taxPriceMode ||
            (session.company.taxPriceMode as "inclusive" | "exclusive"),
          notes: ticketNote.trim() || null,
          lines: cart.map((l) => ({ ...l })),
        };
        const key = localParkedStorageKey(companySlug, terminalId);
        saveLocalParked(key, [local, ...loadLocalParked(key)]);
        wipeCartDraft();
        setCart([]);
        setTenderAmount("");
        setDiscountPct("");
        setTaxRatePct("");
        setTaxPriceMode("");
        setTicketNote("");
        setStatus(`Parked ${parkedNumber} on this register`);
        await refreshParkedList();
        return;
      }
      const parkBody = {
        id: ticketId,
        number: parkedNumber,
        idempotencyKey: idem,
        drawerId: session.drawer.id,
        discountPct: discountPct.trim() || undefined,
        taxRatePct: taxRatePct.trim() || undefined,
        taxPriceMode: taxPriceMode || undefined,
        notes: ticketNote.trim() || undefined,
        cashierUserId: cashierUserId ?? undefined,
        clientCreatedAt: new Date().toISOString(),
        lines: cart.map((l) => ({
          id: newClientId(),
          productId: l.productId,
          qty: String(l.qty),
          unitPrice: l.unitPrice,
          lotCode: l.lotCode,
          serialCode: l.serialCode,
        })),
      };
      let result: Awaited<ReturnType<typeof parkPosTicket>>;
      let rangeAfterPark = mint.nextRange;
      let sessionAfterPark = { ...session, numberRange: mint.nextRange };
      try {
        result = await parkPosTicket(companySlug, terminalId, parkBody);
      } catch (err: unknown) {
        // Local cursor behind server (e.g. prior parks without client mint).
        if (
          !/already allocated/i.test(
            err instanceof Error ? err.message : "",
          )
        ) {
          throw err;
        }
        const again = await hydratePos(companySlug, terminalId, true);
        setCatalog(sortCatalog(again.catalog));
        setCatalogUpdated(again.catalogUpdated);
        const remint = mintTerminalTicketNumber(
          again.session.numberRange
            ? {
                nextSeq: again.session.numberRange.nextSeq,
                rangeEnd: again.session.numberRange.rangeEnd,
                prefix: again.session.numberRange.prefix,
                padWidth: again.session.numberRange.padWidth ?? 4,
              }
            : null,
        );
        if (!remint.ok) {
          setError(
            remint.reason === "exhausted"
              ? "Number range exhausted — go online to refill"
              : "Open this register online once to load ticket numbers",
          );
          return;
        }
        sessionAfterPark = {
          ...again.session,
          numberRange: remint.nextRange,
        };
        await persistSession(sessionAfterPark);
        rangeAfterPark = remint.nextRange;
        result = await parkPosTicket(companySlug, terminalId, {
          ...parkBody,
          number: remint.number,
        });
      }
      // Defense: if server allocated a different seq, catch the local cursor up.
      const syncedRange = advanceNumberRangePast(
        rangeAfterPark,
        result.ticket.number,
      );
      if (
        syncedRange &&
        syncedRange.nextSeq !== rangeAfterPark.nextSeq
      ) {
        await persistSession({
          ...sessionAfterPark,
          numberRange: syncedRange,
        });
      }
      wipeCartDraft();
      setCart([]);
      setTenderAmount("");
      setDiscountPct("");
      setTaxRatePct("");
      setTaxPriceMode("");
      setTicketNote("");
      setStatus(`Parked ${result.ticket.number}`);
      await refreshParkedList();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Park failed");
    } finally {
      setBusy(false);
    }
  }

  async function loadParkIntoCart(parkId: string, fromLocal: boolean) {
    if (!companySlug || !terminalId || !session) return;
    setBusy(true);
    setError(null);
    try {
      if (fromLocal) {
        const key = localParkedStorageKey(companySlug, terminalId);
        const all = loadLocalParked(key);
        const hit = all.find((p) => p.id === parkId);
        if (!hit) {
          setError("Parked ticket not found on this device");
          return;
        }
        saveLocalParked(
          key,
          all.filter((p) => p.id !== parkId),
        );
        applyRecalledLines({
          lines: hit.lines,
          discountPct: hit.discountPct,
          taxRatePct: hit.taxRatePct,
          taxPriceMode: hit.taxPriceMode,
          note: hit.notes,
        });
        await refreshParkedList();
        return;
      }
      const recalled = await recallParkedPosTicket(companySlug, parkId);
      applyRecalledLines({
        lines: recalled.lines.map((l) => ({
          productId: l.productId,
          name: l.name,
          unitPrice: l.unitPrice,
          qty: l.qty,
          trackLot: l.trackLot,
          trackSerial: l.trackSerial,
          lotCode: l.lotCode ?? undefined,
          serialCode: l.serialCode ?? undefined,
        })),
        discountPct: recalled.ticket.discountPct,
        taxRatePct: recalled.ticket.taxRatePct,
        taxPriceMode: recalled.ticket.taxPriceMode,
        note: recalled.ticket.notes,
      });
      await refreshParkedList();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Recall failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRequestRecall(park: ParkedTicketSummary) {
    const fromLocal =
      Boolean(companySlug && terminalId) &&
      loadLocalParked(
        localParkedStorageKey(companySlug!, terminalId!),
      ).some((p) => p.id === park.id);
    if (cart.length === 0) {
      await loadParkIntoCart(park.id, fromLocal);
      return;
    }
    setParkConflict({
      options: buildParkConflictOptions({
        current: {
          key: "current",
          title: "This cart",
          itemCount: cartItemCount(cart),
          dueAmount: moneyDisplay(dueTotal),
        },
        incoming: {
          key: park.id,
          title: park.terminalName,
          itemCount: park.itemCount,
          dueAmount: moneyDisplay(park.grossTotal),
        },
      }),
      incomingId: park.id,
      incomingLocal: fromLocal,
    });
  }

  function focusScanField() {
    if (typeof window === "undefined") return;
    if (!window.matchMedia(POS_COMPACT_MQ).matches) return;
    window.requestAnimationFrame(() => {
      scanInputRef.current?.focus();
    });
  }

  function onScanSubmit() {
    if (!session?.drawer || busy) return;
    const hit = findCatalogByScan(catalog, scan);
    if (!hit) {
      setError(`No product for “${scan.trim()}”`);
      return;
    }
    if ("ambiguous" in hit) {
      setError(`${hit.count} products match — tap one or scan barcode/SKU`);
      return;
    }
    if (d(hit.match.price).lte(0)) {
      setError(`${hit.match.name} has no price`);
      return;
    }
    addProduct(hit.match);
    setStatus(`Added ${hit.match.name}`);
    setScan("");
    focusScanField();
  }

  async function onClearCart() {
    if (cart.length === 0) return;
    const ok = await confirmUi({
      title: "Clear cart?",
      description: "Remove all lines from this ticket.",
      confirmLabel: "Clear",
      destructive: true,
    });
    if (!ok) return;
    wipeCartDraft();
    setCart([]);
    setTenderAmount("");
    setLastSale(null);
    setError(null);
    setStatus(null);
    setSoftLimitNeed(null);
    focusScanField();
  }

  function bumpQty(productId: string, delta: number) {
    setCart((prev) =>
      prev
        .map((l) => {
          if (l.productId !== productId) return l;
          if (l.trackSerial) return l;
          return { ...l, qty: l.qty + delta };
        })
        .filter((l) => l.qty > 0),
    );
  }

  function setCartTracking(
    productId: string,
    patch: { lotCode?: string; serialCode?: string },
  ) {
    setCart((prev) =>
      prev.map((l) =>
        l.productId === productId ? { ...l, ...patch } : l,
      ),
    );
  }

  async function persistSession(next: PosSession) {
    if (!companySlug || !terminalId) return;
    setSession(next);
    await saveSessionCache({
      companyRef: companySlug,
      terminalId,
      session: next,
      savedAt: new Date().toISOString(),
    });
  }

  async function onOpenDrawer() {
    if (!companySlug || !terminalId) return;
    const floatAmt = openingFloat.trim() || "0";
    setBusy(true);
    setError(null);
    const body = {
      id: newClientId(),
      openingFloat: floatAmt,
      clientCreatedAt: new Date().toISOString(),
    };
    try {
      if (!online) {
        await enqueueMutation({
          terminalId,
          kind: "pos.drawer.open",
          id: body.id,
          payload: body,
        });
        const next: PosSession = {
          ...session!,
          drawer: {
            id: body.id,
            status: "open",
            openingFloat: floatAmt,
            openedAt: body.clientCreatedAt,
          },
        };
        await persistSession(next);
        await refreshPending();
        setStatus("Drawer open — saved on this register");
        return;
      }
      try {
        await openPosDrawer(companySlug, terminalId, body);
        await hydrate();
        setStatus("Drawer open");
      } catch (err: unknown) {
        if (!isLikelyNetworkError(err)) throw err;
        await enqueueMutation({
          terminalId,
          kind: "pos.drawer.open",
          id: body.id,
          payload: body,
        });
        const next: PosSession = {
          ...session!,
          drawer: {
            id: body.id,
            status: "open",
            openingFloat: floatAmt,
            openedAt: body.clientCreatedAt,
          },
        };
        await persistSession(next);
        await refreshPending();
        setOnline(false);
        setStatus("Drawer open — queued (offline)");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function onCloseDrawer() {
    if (!companySlug || !terminalId || !session?.drawer) return;
    const expected =
      session.drawer.expectedCash ?? session.drawer.openingFloat ?? "0";
    const raw = window.prompt(`Cash counted (expected ${expected})`, expected);
    if (raw == null) return;
    const counted = raw.trim();
    if (counted === "" || Number.isNaN(Number(counted))) {
      setError("Enter a valid cash count");
      return;
    }
    setBusy(true);
    setError(null);
    const body: {
      closingCashCounted: string;
      confirmVariance?: boolean;
    } = { closingCashCounted: counted };
    if (Number(counted) !== Number(expected)) {
      const ok = await confirmUi({
        title: "Cash variance?",
        description: `Counted ${counted}, expected ${expected}.\n\nClose anyway?`,
        confirmLabel: "Close anyway",
        destructive: true,
      });
      if (!ok) {
        setBusy(false);
        setError("Close cancelled");
        return;
      }
      body.confirmVariance = true;
    }
    try {
      if (!online) {
        await enqueueMutation({
          terminalId,
          kind: "pos.drawer.close",
          payload: body,
        });
        await persistSession({ ...session!, drawer: null });
        setCart([]);
        await refreshPending();
        setStatus("Drawer closed — will sync");
        return;
      }
      try {
        try {
          await closePosDrawer(companySlug, terminalId, body);
        } catch (err: unknown) {
          if (!isApiError(err) || apiErrorCode(err) !== "drawer_variance") {
            throw err;
          }
          const ok = await confirmUi({
            title: "Drawer variance?",
            description: `${err.message}\n\nClose drawer with this variance?`,
            confirmLabel: "Close anyway",
            destructive: true,
          });
          if (!ok) {
            setError("Close cancelled");
            return;
          }
          await closePosDrawer(companySlug, terminalId, {
            ...body,
            confirmVariance: true,
          });
        }
        setCart([]);
        await hydrate();
        setStatus("Drawer closed");
      } catch (err: unknown) {
        if (!isLikelyNetworkError(err)) throw err;
        await enqueueMutation({
          terminalId,
          kind: "pos.drawer.close",
          payload: body,
        });
        await persistSession({ ...session!, drawer: null });
        setCart([]);
        await refreshPending();
        setOnline(false);
        setStatus("Drawer closed — queued (offline)");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSell() {
    if (!companySlug || !terminalId || !session?.drawer || !payMethodId) return;
    if (cart.length === 0) {
      setError("Add items first");
      return;
    }
    const pay = session.payMethods.find((p) => p.id === payMethodId);
    if (!online && pay && !pay.worksOffline) {
      setError(`${pay.name} needs network — use Cash while offline`);
      return;
    }

    const discForDue = discountPct.trim() ? Number(discountPct) : 0;
    const due =
      Number.isFinite(discForDue) && discForDue > 0
        ? d(applyDiscountPct(cartTotal, discForDue).discounted)
        : cartTotal;
    const tender = tenderAmount.trim() ? d(tenderAmount) : due;
    if (tender.lt(due)) {
      setError("Tender is less than total");
      return;
    }

    const cartSnapshot = [...cart];
    // Soft visual warn always; hard-block only when org allowOversell is off.
    if (session.company.allowOversell === false) {
      const short = cartSnapshot.find((line) => {
        const cat = catalog.find((c) => c.id === line.productId);
        if (!cat || cat.kind === "service") return false;
        return d(line.qty).gt(d(cat.onHand ?? 0));
      });
      if (short) {
        const cat = catalog.find((c) => c.id === short.productId);
        setError(
          `Stock short (on hand ${qtyDisplay(cat?.onHand ?? 0)} — selling ${qtyDisplay(short.qty)}). Allow oversell is off in Org settings.`,
        );
        return;
      }
    }

    const discPctNum = discountPct.trim() ? Number(discountPct) : 0;
    const softCap = session.company.discountSoftCapPct;
    let confirmDiscountOverCap = Boolean(
      offlinePinApprove?.permissionKey === "pos.discount_over_cap",
    );
    if (
      Number.isFinite(discPctNum) &&
      discPctNum > softCap &&
      !overrideRequestId &&
      !confirmDiscountOverCap
    ) {
      if (!online) {
        const path = offlineSoftLimitPath({
          canSelfAllow: await currentCashierCanSelfAllow(
            "pos.discount_over_cap",
          ),
          hasCachedApprover: await hasCachedApproverFor(
            "pos.discount_over_cap",
          ),
        });
        if (path === "blocked") {
          setError(offlineSoftLimitBlockedMessage("pos.discount_over_cap"));
          return;
        }
        if (path === "need_approver_pin") {
          setSoftLimitNeed("discount");
          setStatus(
            `Discount over cap — need manager PIN on this register`,
          );
          return;
        }
      }
      const ok = await confirmUi({
        title: "Discount over soft cap?",
        description: `Discount ${discPctNum}% exceeds soft cap (${softCap}%).\n\nCharge anyway?`,
        confirmLabel: "Charge anyway",
        destructive: true,
      });
      if (!ok) {
        setError("Sale cancelled — soft cap");
        return;
      }
      confirmDiscountOverCap = true;
    }

    const mint = mintTerminalTicketNumber(
      session.numberRange
        ? {
            nextSeq: session.numberRange.nextSeq,
            rangeEnd: session.numberRange.rangeEnd,
            prefix: session.numberRange.prefix,
            padWidth: session.numberRange.padWidth ?? 4,
          }
        : null,
    );
    if (!mint.ok) {
      setError(
        mint.reason === "exhausted"
          ? "Number range exhausted — go online to refill"
          : "Open this register online once to load ticket numbers",
      );
      return;
    }
    const ticketNumber = mint.number;
    const nextRange = mint.nextRange;

    setBusy(true);
    setError(null);
    const ticketId = newClientId();
    const discountSnapshot = discountPct.trim() || undefined;
    const notesSnapshot = ticketNote.trim() || undefined;
    const payName = pay?.name ?? "Cash";
    await persistSession({
      ...session,
      numberRange: nextRange,
    });

    const effectiveTaxRate =
      taxRatePct.trim() || String(session.company.taxRatePct);
    const effectiveTaxMode =
      taxPriceMode || session.company.taxPriceMode;

    const sellBody = {
      id: ticketId,
      number: ticketNumber,
      cashierUserId: cashierUserId ?? undefined,
      idempotencyKey: `pos-sell-${ticketId}`,
      drawerId: session.drawer.id,
      clientCreatedAt: new Date().toISOString(),
      discountPct: discountSnapshot,
      ...(taxRatePct.trim() ? { taxRatePct: taxRatePct.trim() } : {}),
      ...(taxPriceMode ? { taxPriceMode } : {}),
      ...(notesSnapshot ? { notes: notesSnapshot } : {}),
      overrideRequestId:
        overrideRequestId ?? offlinePinApprove?.id ?? undefined,
      ...(offlinePinApprove
        ? { offlinePinApprove }
        : {}),
      ...(confirmDiscountOverCap
        ? { confirmDiscountOverCap: true as const }
        : {}),
      lines: cartSnapshot.map((l) => ({
        id: newClientId(),
        productId: l.productId,
        qty: String(l.qty),
        unitPrice: l.unitPrice,
        ...(l.lotCode?.trim() ? { lotCode: l.lotCode.trim() } : {}),
        ...(l.serialCode?.trim() ? { serialCode: l.serialCode.trim() } : {}),
      })),
      tender: {
        id: newClientId(),
        payMethodId,
        amount: money(tender),
      },
    };

    function receiptBase(extra?: {
      number?: string;
      provisional?: boolean;
      netTotal?: string;
      taxTotal?: string;
      grossTotal?: string;
      changeGiven?: string;
    }): PosReceiptData {
      return buildReceipt({
        companyName:
          session!.company.name ||
          session!.terminal.branchName ||
          session!.terminal.name,
        terminalName: session!.terminal.name,
        number: extra?.number ?? provisionalTicketNumber(ticketId),
        provisional: extra?.provisional ?? true,
        issuedAt: sellBody.clientCreatedAt,
        lines: cartSnapshot.map((l) => ({
          name: l.name,
          qty: l.qty,
          unitPrice: l.unitPrice,
        })),
        discountPct: discountSnapshot,
        taxRatePct: effectiveTaxRate,
        taxPriceMode: effectiveTaxMode,
        payMethodName: payName,
        tenderAmount: money(tender),
        netTotal: extra?.netTotal,
        taxTotal: extra?.taxTotal,
        grossTotal: extra?.grossTotal,
        changeGiven: extra?.changeGiven,
        currencyCode: session!.company.currencyCode,
      });
    }

    async function finishLocalSale(label: string) {
      const nextCat = sortCatalog(
        applyLocalStockDelta(catalog, sellBody.lines),
      );
      setCatalog(nextCat);
      if (companySlug && terminalId) {
        const cached = await loadCatalogCache(companySlug, terminalId);
        await saveCatalogCache({
          companyRef: companySlug,
          terminalId,
          items: nextCat,
          watermark: cached?.watermark ?? {
            lastUpdated: catalogUpdated,
            lastId: null,
          },
          savedAt: new Date().toISOString(),
        });
        await cacheTicketForReturn({
          ticketId,
          terminalId,
          number: ticketNumber,
          warehouseId: session!.terminal.warehouseId,
          lines: sellBody.lines.map((l) => ({
            sourceLineId: l.id,
            productId: l.productId,
            qty: l.qty,
            lotCode: l.lotCode ?? null,
            serialCode: l.serialCode ?? null,
          })),
          savedAt: new Date().toISOString(),
        });
      }
      const localReceipt = receiptBase({
        number: ticketNumber,
        provisional: false,
      });
      setReceipt(localReceipt);
      setLastSale(
        `${localReceipt.number} · ₱${moneyDisplay(localReceipt.grossTotal)}`,
      );
      setRecentTickets((prev) =>
        [
          {
            id: ticketId,
            number: localReceipt.number,
            lifecycle: "posted",
            grossTotal: localReceipt.grossTotal,
          },
          ...prev.filter((t) => t.id !== ticketId),
        ].slice(0, 5),
      );
      setStatus(label);
      wipeCartDraft();
      setCart([]);
      setTenderAmount("");
      setDiscountPct("");
      setTaxPriceMode("");
      setTaxRatePct("");
      setTicketNote("");
      setOverrideRequestId(null);
      setOfflinePinApprove(null);
      setSoftLimitNeed(null);
      setTenderPadOpen(false);
      await refreshPending();
      focusScanField();
    }

    try {
      if (!online) {
        await enqueueMutation({
          terminalId,
          kind: "pos.sell",
          id: ticketId,
          payload: sellBody,
        });
        await finishLocalSale("Saved on this register — will sync");
        return;
      }
      try {
        let result: Awaited<ReturnType<typeof sellPosTicket>>;
        try {
          result = await sellPosTicket(companySlug, terminalId, sellBody);
        } catch (err: unknown) {
          const code = apiErrorCode(err);
          const msg = isApiError(err)
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed";
          if (code === "discount_over_cap") {
            const ok = await confirmUi({
              title: "Discount over soft cap?",
              description: `${msg}\n\nCharge with this discount over the soft cap?`,
              confirmLabel: "Charge anyway",
              destructive: true,
            });
            if (!ok) {
              setError("Sale cancelled");
              return;
            }
            result = await sellPosTicket(companySlug, terminalId, {
              ...sellBody,
              confirmDiscountOverCap: true,
            });
          } else if (code === "discount_over_cap_override") {
            setError(msg);
            setSoftLimitNeed("discount");
            setStatus("Need discount override — request below");
            return;
          } else if (/already allocated/i.test(msg)) {
            // Local cursor behind server — refresh range and remint once.
            const again = await hydratePos(companySlug, terminalId, true);
            setCatalog(sortCatalog(again.catalog));
            setCatalogUpdated(again.catalogUpdated);
            const remint = mintTerminalTicketNumber(
              again.session.numberRange
                ? {
                    nextSeq: again.session.numberRange.nextSeq,
                    rangeEnd: again.session.numberRange.rangeEnd,
                    prefix: again.session.numberRange.prefix,
                    padWidth: again.session.numberRange.padWidth ?? 4,
                  }
                : null,
            );
            if (!remint.ok) {
              setError(
                remint.reason === "exhausted"
                  ? "Number range exhausted — go online to refill"
                  : "Open this register online once to load ticket numbers",
              );
              return;
            }
            await persistSession({
              ...again.session,
              numberRange: remint.nextRange,
            });
            const retryId = newClientId();
            result = await sellPosTicket(companySlug, terminalId, {
              ...sellBody,
              id: retryId,
              number: remint.number,
              idempotencyKey: `pos-sell-${retryId}`,
            });
          } else {
            throw err;
          }
        }
        setSoftLimitNeed(null);
        const posted = receiptBase({
          number: result.ticket.number,
          provisional: false,
          netTotal: result.ticket.netTotal,
          taxTotal: result.ticket.taxTotal,
          grossTotal: result.ticket.grossTotal,
          changeGiven: result.ticket.changeGiven,
        });
        setReceipt(posted);
        const change = posted.changeGiven;
        const cur = session.company.currencyCode;
        setLastSale(
          `${posted.number} · ${cur} ${moneyDisplay(posted.grossTotal)}` +
            (d(change).gt(0)
              ? ` · change ${cur} ${moneyDisplay(change)}`
              : ""),
        );
        if (result.glError) {
          setError(
            `Posted ${posted.number} but GL: ${result.glError}`,
          );
          setStatus("Sale posted — GL needs attention");
        } else if (result.oversell || result.oversellProductIds?.length) {
          setStatus("Sold — below recorded on-hand");
        } else {
          setStatus(result.replayed ? "Replayed (idempotent)" : "Sale posted");
        }
        wipeCartDraft();
        setCart([]);
        setTenderAmount("");
        setDiscountPct("");
        setTaxPriceMode("");
        setTaxRatePct("");
        setTicketNote("");
        setOverrideRequestId(null);
        const again = await hydratePos(companySlug, terminalId, true);
        setCatalog(sortCatalog(again.catalog));
        setCatalogUpdated(again.catalogUpdated);
        setSession(again.session);
      } catch (err: unknown) {
        if (!isLikelyNetworkError(err)) throw err;
        await enqueueMutation({
          terminalId,
          kind: "pos.sell",
          id: ticketId,
          payload: sellBody,
        });
        setOnline(false);
        await finishLocalSale("Queued — will sync when online");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRequestDiscountOverride() {
    if (!companySlug || !terminalId) return;
    if (!online) {
      setSoftLimitNeed("discount");
      setStatus("Need manager PIN on this register (offline)");
      return;
    }
    setBusy(true);
    setError(null);
    const base = {
      permissionKey: "pos.discount_over_cap",
      summary: `POS discount ${discountPct || "?"}% (cap ${session?.company.discountSoftCapPct}%)`,
      terminalId,
      payload: { discountPct },
      grantScope: "transaction" as const,
    };
    try {
      let request: { id: string; status: string };
      try {
        ({ request } = await createOverrideRequest(companySlug, {
          ...base,
          autoApprove: true,
        }));
      } catch {
        ({ request } = await createOverrideRequest(companySlug, base));
      }
      setOverrideRequestId(request.id);
      setStatus(
        request.status === "approved"
          ? "Override granted — Charge again"
          : "Override requested — Approvals or OTP below",
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(false);
    }
  }

  async function persistCatalog(nextCat: PosCatalogItem[]) {
    setCatalog(nextCat);
    if (!companySlug || !terminalId) return;
    const cached = await loadCatalogCache(companySlug, terminalId);
    await saveCatalogCache({
      companyRef: companySlug,
      terminalId,
      items: nextCat,
      watermark: cached?.watermark ?? {
        lastUpdated: catalogUpdated,
        lastId: null,
      },
      savedAt: new Date().toISOString(),
    });
  }

  async function onReturnTicket(ticketId: string, number: string) {
    if (!companySlug || !terminalId) return;
    if (online) {
      navigate(
        `/c/${companySlug}/returns/new?sourceType=pos_ticket&sourceId=${ticketId}`,
      );
      return;
    }
    const ok = await confirmUi({
      title: `Return ${number} offline?`,
      description: "Return all lines. Cash refund will sync when online.",
      confirmLabel: "Return",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const result = await enqueueOfflineReturn({
        terminalId,
        ticketId,
        number,
      });
      if (!result.ok) {
        setError(result.reason);
        return;
      }
      const nextCat = sortCatalog(
        reverseLocalStockDelta(
          catalog,
          result.lines.map((l) => ({
            productId: l.productId,
            qty: l.qty,
          })),
        ),
      );
      await persistCatalog(nextCat);
      setRecentTickets((prev) => prev.filter((t) => t.id !== ticketId));
      await refreshPending();
      setStatus(`Return ${number} queued — will sync`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Return failed");
    } finally {
      setBusy(false);
    }
  }

  async function onVoidTicket(ticketId: string, number: string) {
    if (!companySlug || !terminalId) return;
    const ok = await confirmUi({
      title: `Void ${number}?`,
      description:
        "Stock will restock. Voiding is permanent — you cannot undo this ticket.",
      confirmLabel: "Void",
      destructive: true,
    });
    if (!ok) return;
    const entered = window.prompt(`Reason for voiding ${number}:`, "POS void");
    if (entered == null) return;
    const reason = entered.trim() || "POS void";
    setBusy(true);
    setError(null);
    try {
      const voidOfflinePin =
        offlinePinApprove?.permissionKey === "pos.void"
          ? offlinePinApprove
          : null;
      if (!online) {
        const result = await voidLocalOrEnqueue({
          terminalId,
          ticketId,
          reason,
          overrideRequestId: voidOverrideId,
          offlinePinApprove: voidOfflinePin,
        });
        if (result.mode === "dropped" && result.lines.length > 0) {
          await persistCatalog(
            sortCatalog(reverseLocalStockDelta(catalog, result.lines)),
          );
        }
        setRecentTickets((prev) => prev.filter((t) => t.id !== ticketId));
        setVoidOverrideId(null);
        setOfflinePinApprove(null);
        await refreshPending();
        setStatus(
          result.mode === "dropped"
            ? `Voided ${number} on this register`
            : `Voided ${number} — will sync`,
        );
        return;
      }
      try {
        await voidPosTicket(companySlug, ticketId, {
          reason,
          idempotencyKey: `void-${ticketId}`,
          overrideRequestId: voidOverrideId ?? undefined,
          ...(voidOfflinePin ? { offlinePinApprove: voidOfflinePin } : {}),
          fromTerminal: true,
        });
        setStatus(`Voided ${number}`);
        setVoidOverrideId(null);
        setOfflinePinApprove(null);
        await reloadTickets();
      } catch (err: unknown) {
        if (!isLikelyNetworkError(err)) throw err;
        const result = await voidLocalOrEnqueue({
          terminalId,
          ticketId,
          reason,
          overrideRequestId: voidOverrideId,
          offlinePinApprove: voidOfflinePin,
        });
        if (result.mode === "dropped" && result.lines.length > 0) {
          await persistCatalog(
            sortCatalog(reverseLocalStockDelta(catalog, result.lines)),
          );
        }
        setRecentTickets((prev) => prev.filter((t) => t.id !== ticketId));
        setVoidOverrideId(null);
        setOfflinePinApprove(null);
        setOnline(false);
        await refreshPending();
        setStatus(
          result.mode === "dropped"
            ? `Voided ${number} on this register`
            : `Voided ${number} — queued offline`,
        );
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Void failed";
      if (apiErrorCode(err) === "needs_override") {
        setError(msg);
        setStatus("Need pos.void or override — request below");
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  async function onRequestVoidOverride(ticketId: string, number: string) {
    if (!companySlug || !terminalId) return;
    if (!online) {
      setError("Void override needs network — void unsynced sales locally");
      return;
    }
    setBusy(true);
    setError(null);
    const base = {
      permissionKey: "pos.void",
      summary: `Void ${number}`,
      terminalId,
      docType: "pos_ticket",
      docId: ticketId,
      grantScope: "transaction" as const,
    };
    try {
      let request: { id: string; status: string };
      try {
        ({ request } = await createOverrideRequest(companySlug, {
          ...base,
          autoApprove: true,
        }));
      } catch {
        ({ request } = await createOverrideRequest(companySlug, base));
      }
      setVoidOverrideId(request.id);
      setStatus(
        request.status === "approved"
          ? "Void override granted — Void again"
          : "Void override requested — Approvals or OTP below",
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRedeemOtp() {
    if (!companySlug || !otpCode.trim()) return;
    if (!online) {
      setError("OTP redeem needs network");
      return;
    }
    const requestId = voidOverrideId ?? overrideRequestId ?? undefined;
    setBusy(true);
    setError(null);
    try {
      const { request } = await redeemOverrideOtp(companySlug, {
        code: otpCode.trim(),
        ...(requestId ? { requestId } : {}),
      });
      if (request.permissionKey === "pos.void") {
        setVoidOverrideId(request.id);
        setStatus("OTP accepted — Void again");
      } else {
        setOverrideRequestId(request.id);
        setStatus("OTP accepted — Charge again");
      }
      setOtpCode("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "OTP failed");
    } finally {
      setBusy(false);
    }
  }

  async function onApproveWithPin() {
    if (!companySlug || !terminalId) return;
    if (!approverCode.trim() || approverPin.length < 4) {
      setError("Enter manager user code and PIN");
      return;
    }
    const permissionKey =
      softLimitNeed === "discount"
        ? "pos.discount_over_cap"
        : voidOverrideId
          ? "pos.void"
          : null;
    const requestId = voidOverrideId ?? overrideRequestId;

    setBusy(true);
    setError(null);
    try {
      if (!online) {
        if (!permissionKey) {
          setError("Nothing to approve — Charge again after a soft-limit warn");
          return;
        }
        const code = normalizeUserCode(approverCode);
        if (!code) {
          setError("Invalid manager user code");
          return;
        }
        const cached = await loadCachedCashier(companySlug, terminalId, code);
        if (!cached || !cachedUserCanApprove(cached.canApproveKeys, permissionKey)) {
          setError(
            "Manager not cached for this override — approve once online to cache PIN",
          );
          return;
        }
        if (cached.userId === cashierUserId) {
          setError("Cannot approve your own override");
          return;
        }
        const ok = await verifyPinAgainstCached({
          companyRef: companySlug,
          terminalId,
          userCode: code,
          pin: approverPin,
          saltHex: cached.saltHex,
          verifierHex: cached.verifierHex,
        });
        if (!ok) {
          setError("Invalid code or PIN");
          return;
        }
        const grantId = crypto.randomUUID();
        setOfflinePinApprove({
          id: grantId,
          permissionKey,
          approverUserId: cached.userId,
          summary: `${permissionKey} · offline on-device PIN`,
        });
        setOverrideRequestId(grantId);
        if (permissionKey === "pos.void") setVoidOverrideId(grantId);
        setSoftLimitNeed(null);
        setApproverPin("");
        setStatus("Override granted on this register — Charge / Void again");
        return;
      }

      if (!requestId) {
        setError("Request an override first");
        return;
      }
      const { request } = await approveOverrideWithPin(companySlug, requestId, {
        userCode: approverCode.trim(),
        pin: approverPin,
        grantScope: "transaction",
      });
      const code = normalizeUserCode(approverCode);
      if (code && request.approverUserId) {
        await cacheCashierPin({
          userCode: code,
          userId: request.approverUserId,
          displayName: code,
          pin: approverPin,
          permissionKeys: [
            "can_approve",
            softLimitNeed === "discount"
              ? "pos.discount_over_cap"
              : "pos.void",
          ],
        });
      }
      const kind =
        voidOverrideId && voidOverrideId === requestId ? "void" : "sale";
      setStatus(overrideWaitMessage(request.status, kind));
      setApproverPin("");
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "PIN approve failed");
    } finally {
      setBusy(false);
    }
  }

  async function cacheCashierPin(input: {
    userCode: string;
    userId: string;
    displayName: string;
    pin: string;
    permissionKeys?: string[];
  }) {
    if (!companySlug || !terminalId) return;
    const saltHex = newSaltHex();
    const verifierHex = await makePinVerifier({
      companyRef: companySlug,
      terminalId,
      userCode: input.userCode,
      pin: input.pin,
      saltHex,
    });
    const permissionKeys = input.permissionKeys ?? [];
    await saveCachedCashier({
      companyRef: companySlug,
      terminalId,
      userCode: input.userCode,
      userId: input.userId,
      displayName: input.displayName,
      saltHex,
      verifierHex,
      permissionKeys,
      canApproveKeys: deriveCanApproveKeys(permissionKeys),
      savedAt: new Date().toISOString(),
    });
  }

  async function hasCachedApproverFor(permissionKey: string): Promise<boolean> {
    if (!companySlug || !terminalId) return false;
    const rows = await listCachedCashiers(companySlug, terminalId);
    return rows.some((r) => cachedUserCanApprove(r.canApproveKeys, permissionKey));
  }

  async function currentCashierCanSelfAllow(
    permissionKey: string,
  ): Promise<boolean> {
    if (!companySlug || !terminalId || !cashierUserId) return false;
    const rows = await listCachedCashiers(companySlug, terminalId);
    const me = rows.find((r) => r.userId === cashierUserId);
    return cachedUserCanSelfAllow(me?.permissionKeys, permissionKey);
  }

  async function onCashierLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!companySlug || !terminalId) return;
    const code = normalizeUserCode(pinUserCode);
    if (!code) {
      setError("Invalid user code");
      return;
    }
    if (pinValue.length < 4) {
      setError("PIN must be at least 4 characters");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (!online) {
        const cached = await loadCachedCashier(companySlug, terminalId, code);
        if (!cached) {
          setError(
            "This cashier isn’t cached on this register — sign in once while online",
          );
          return;
        }
        const ok = await verifyPinAgainstCached({
          companyRef: companySlug,
          terminalId,
          userCode: code,
          pin: pinValue,
          saltHex: cached.saltHex,
          verifierHex: cached.verifierHex,
        });
        if (!ok) {
          setError("Invalid code or PIN");
          return;
        }
        rememberCashier(cached.displayName, cached.userId);
        setPinUserCode("");
        setPinValue("");
        setStatus(`Cashier ${cached.displayName} (offline)`);
        return;
      }

      try {
        const pinLogin = await loginWithPin({
          companyRef: companySlug,
          userCode: code,
          pin: pinValue,
        });
        const { user } = pinLogin;
        const label = user.displayName ?? user.email ?? code;
        await cacheCashierPin({
          userCode: code,
          userId: user.id,
          displayName: label,
          pin: pinValue,
          permissionKeys: pinLogin.permissionKeys ?? [],
        });
        rememberCashier(label, user.id);
        setPinUserCode("");
        setPinValue("");
        await hydrate();
        setStatus(`Cashier ${label}`);
      } catch (err: unknown) {
        if (!isLikelyNetworkError(err)) throw err;
        setOnline(false);
        const cached = await loadCachedCashier(companySlug, terminalId, code);
        if (!cached) {
          setError(
            "Offline — this cashier isn’t cached yet. Connect once to cache PIN.",
          );
          return;
        }
        const ok = await verifyPinAgainstCached({
          companyRef: companySlug,
          terminalId,
          userCode: code,
          pin: pinValue,
          saltHex: cached.saltHex,
          verifierHex: cached.verifierHex,
        });
        if (!ok) {
          setError("Invalid code or PIN");
          return;
        }
        rememberCashier(cached.displayName, cached.userId);
        setPinUserCode("");
        setPinValue("");
        setStatus(`Cashier ${cached.displayName} (offline)`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "PIN login failed");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Lock register to cashier PIN screen (kiosk stay open).
   * Cart clear is warned — plan/19 cashier logout / switch.
   */
  async function onLockToPinLogin(kind: "exit" | "switch") {
    if (!companySlug || !terminalId) return;
    if (cart.length > 0) {
      const ok = await confirmUi({
        title: kind === "exit" ? "Exit to PIN login?" : "Switch cashier?",
        description:
          "Cart still has items — they’ll be cleared. Sale is not charged.",
        confirmLabel: kind === "exit" ? "Exit" : "Switch",
        destructive: true,
      });
      if (!ok) return;
    } else if (kind === "exit") {
      const ok = await confirmUi({
        title: "Exit to PIN login?",
        description:
          "Next person enters user code + PIN. Logout (Google) is on that screen if you need to leave POS mode.",
        confirmLabel: "Exit",
      });
      if (!ok) return;
    }
    clearCashier();
    wipeCartDraft();
    setCart([]);
    setTenderAmount("");
    setDiscountPct("");
    setError(null);
    setStatus(
      kind === "exit"
        ? "Signed out of register — enter user code + PIN"
        : "Enter next cashier code + PIN",
    );
  }

  /**
   * Leave POS mode entirely — plan/19: Google again so a cashier PIN cannot
   * open the back office. Only from the PIN screen (Logout).
   */
  async function onLeavePosMode() {
    if (!companySlug) return;
    const ok = await confirmUi({
      title: "Logout of POS mode?",
      description:
        "Signs out and returns to Google login. A host must reopen this terminal before anyone can sell again.",
      confirmLabel: "Logout",
      destructive: true,
    });
    if (!ok) return;
    clearCashier();
    await logout().catch(() => null);
    navigate("/login", { replace: true });
  }

  const offlineMethods: PosPayMethod[] =
    session?.payMethods.filter((p) => (!online ? p.worksOffline : true)) ??
    [];

  if (!companySlug || !terminalId) return null;

  if (!session) {
    return (
      <div className="mx-auto min-h-0 w-full max-w-sm flex-1 space-y-4 overflow-y-auto py-16">
        {error ? (
          <Alert variant="destructive">{error}</Alert>
        ) : (
          <Alert variant="muted">Loading register…</Alert>
        )}
        {needsRebind ? (
          <div className="space-y-3">
            <p className="m-0 text-sm leading-relaxed text-[var(--color-muted)]">
              Use this device if you meant to move the register here — or open
              POS from the bound tablet, or Unbind under Terminals.
            </p>
            <Button
              type="button"
              className="w-full"
              pending={busy}
              pendingLabel="Moving…"
              onClick={() => void onUseThisDevice()}
            >
              Use this device
            </Button>
            {companySlug ? (
              <Button asChild variant="outline" className="w-full">
                <Link to={`/c/${companySlug}/pos`} className="no-underline">
                  Back to terminals
                </Link>
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  if (!cashierName) {
    return (
      <div className="mx-auto min-h-0 w-full max-w-sm flex-1 animate-[softRise_320ms_ease-out] overflow-y-auto py-10">
        <h1 className="m-0 font-[family-name:var(--font-display)] text-2xl font-semibold tracking-tight">
          {session.terminal.name}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-[var(--color-muted)]">
          Enter the cashier’s{" "}
          <strong className="font-semibold text-[var(--color-foreground)]">
            user code + PIN
          </strong>{" "}
          from People &amp; PINs. No Google account needed for the cashier.
        </p>
        <p className="mt-2 text-xs leading-relaxed text-[var(--color-muted)]">
          A manager already opened this terminal with Google. Cashiers cannot
          start POS from login alone.
          {!online
            ? " Offline: only cashiers previously signed in on this register."
            : " Online sign-in caches this cashier for offline switch."}
        </p>
        <form
          className="mt-8 space-y-4"
          onSubmit={(e) => void onCashierLogin(e)}
        >
          <div className="space-y-1.5">
            <Label htmlFor="pos-pin-code">User code</Label>
            <Input
              id="pos-pin-code"
              className="min-h-11 font-mono tracking-widest"
              inputMode="numeric"
              autoComplete="username"
              value={pinUserCode}
              onChange={(e) => setPinUserCode(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pos-pin-value">PIN</Label>
            <Input
              id="pos-pin-value"
              type="password"
              className="min-h-11 font-mono tracking-widest"
              inputMode="numeric"
              autoComplete="current-password"
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value)}
              required
              minLength={4}
            />
          </div>
          {error && <Alert variant="destructive">{error}</Alert>}
          <Button
            type="submit"
            className="min-h-11 w-full"
            disabled={
              busy || pinUserCode.trim().length < 1 || pinValue.length < 4
            }
          >
            {busy ? "…" : "Start selling"}
          </Button>
        </form>
        <div className="mt-8 space-y-2 text-center">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={busy}
            onClick={() => void onLeavePosMode()}
          >
            Logout
          </Button>
          <p className="m-0 text-xs text-[var(--color-muted)]">
            Leaves POS mode — Google sign-in required to reopen this terminal.
          </p>
        </div>
      </div>
    );
  }

  const selectedPay =
    (online ? session.payMethods : offlineMethods).find(
      (p) => p.id === payMethodId,
    ) ?? null;
  const cashTender = payMethodUsesCashBills({
    code: selectedPay?.code,
    name: selectedPay?.name,
  });
  const tenderQuickAll = suggestedTenderFills(moneyDisplay(dueTotal));
  const tenderQuick = cashTender ? tenderQuickAll : tenderQuickAll.slice(0, 1);

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-hidden",
        compactCatalog
          ? "gap-2"
          : "gap-4 lg:min-h-0 lg:flex-row lg:items-stretch",
      )}
    >
      {receipt && (
        <div className="pos-receipt-print" aria-hidden>
          <PosReceipt data={receipt} />
        </div>
      )}
      <section
        className={cn(
          "no-print flex min-h-0 flex-col",
          compactCatalog && session.drawer
            ? "max-h-[min(30vh,12.5rem)] shrink-0 overflow-hidden"
            : compactCatalog
              ? "shrink-0"
              : "min-h-0 flex-1 overflow-hidden lg:min-h-0",
        )}
      >
        <div className="mb-2 flex shrink-0 flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-[family-name:var(--font-display)] text-base font-semibold tracking-tight sm:text-lg">
              {session.terminal.name}
            </div>
            <div className="mt-0.5 truncate text-xs text-[var(--color-muted)]">
              {cashierName}
              {compactCatalog
                ? ` · ${session.company.currencyCode}`
                : ` · ${session.terminal.branchName ?? "Branch"} · ${session.company.currencyCode} · tax ${
                    taxRatePct.trim()
                      ? `${taxRatePct.trim()}% `
                      : `${Number(session.company.taxRatePct)}% `
                  }${taxPriceMode || session.company.taxPriceMode}${
                    taxRatePct.trim() || taxPriceMode ? " (override)" : ""
                  }${
                    catalogUpdated
                      ? ` · catalog ${new Date(catalogUpdated).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}`
                      : ""
                  }`}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {compactCatalog ? (
              <>
                {!session.drawer ? (
                  <Button
                    type="button"
                    size="sm"
                    className="min-h-9"
                    disabled={busy}
                    onClick={() => void onOpenDrawer()}
                  >
                    Open drawer
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="min-h-9"
                  disabled={busy}
                  onClick={() => setMoreOpen(true)}
                >
                  More
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="min-h-9"
                  disabled={busy}
                  onClick={() => void onLockToPinLogin("switch")}
                >
                  Switch cashier
                </Button>
                {!session.drawer ? (
                  <Button
                    type="button"
                    className="min-h-11"
                    disabled={busy}
                    onClick={() => void onOpenDrawer()}
                  >
                    Open drawer
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11"
                    disabled={busy || cart.length > 0}
                    title={
                      cart.length > 0
                        ? "Clear or charge the cart before closing"
                        : undefined
                    }
                    onClick={() => void onCloseDrawer()}
                  >
                    Close drawer
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11"
                  disabled={busy}
                  onClick={() => void onLockToPinLogin("exit")}
                >
                  Exit
                </Button>
              </>
            )}
          </div>
          {session.drawer && cart.length > 0 && !compactCatalog && (
            <p className="m-0 w-full text-xs text-[var(--color-muted)]">
              Clear or complete the cart before closing the drawer.
            </p>
          )}
        </div>

        {!session.drawer && (
          <div className="space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-card)]/80 px-4 py-4 text-sm shadow-[0_1px_0_color-mix(in_oklab,var(--color-foreground)_4%,transparent)]">
            <p className="m-0 leading-relaxed text-[var(--color-muted)]">
              Open the drawer to start selling. Exit POS returns to PIN login;
              use Logout there to leave POS mode (Google).
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="pos-opening-float">
                Opening float ({session.company.currencyCode})
              </Label>
              <Input
                id="pos-opening-float"
                className="max-w-[10rem] font-mono"
                inputMode="decimal"
                value={openingFloat}
                onChange={(e) => setOpeningFloat(e.target.value)}
              />
            </div>
          </div>
        )}

        <form
          className="mb-2 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            onScanSubmit();
          }}
        >
          <Input
            ref={scanInputRef}
            className="min-h-11 flex-1 font-mono"
            placeholder={
              compactCatalog
                ? "Scan or search"
                : "Scan barcode or type SKU / name"
            }
            value={scan}
            disabled={!session.drawer || busy}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            autoFocus={compactCatalog && Boolean(session.drawer)}
            onChange={(e) => setScan(e.target.value)}
          />
          <Button
            type="submit"
            variant="outline"
            className="min-h-11"
            disabled={!session.drawer || busy || !scan.trim()}
          >
            Add
          </Button>
        </form>

        {compactCatalog ? (
          <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
            <p className="m-0 text-xs text-[var(--color-muted)]">
              {scan.trim().length >= 2
                ? "Matches"
                : favoriteIds.length || recentIds.length
                  ? "Favorites & recent"
                  : "Scan, search, or browse"}
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="min-h-9"
              disabled={!session.drawer || busy || catalog.length === 0}
              onClick={() => setBrowseOpen(true)}
            >
              Browse
            </Button>
          </div>
        ) : null}

        <div
          className={cn(
            "grid min-h-0 gap-2 overflow-auto",
            compactCatalog
              ? "max-h-full flex-1 grid-cols-2 content-start"
              : "flex-1 grid-cols-2 sm:grid-cols-3 md:grid-cols-4",
          )}
        >
          {(compactCatalog ? compactRailProducts : catalog).map((p) => (
            <PosProductTile
              key={p.id}
              product={p}
              cartQty={cartQtyByProduct.get(p.id) ?? 0}
              favorited={favoriteSet.has(p.id)}
              disabled={!session.drawer || busy || d(p.price).lte(0)}
              compact={compactCatalog}
              onAdd={() => addProduct(p)}
              onToggleFavorite={() => onToggleFavorite(p.id)}
            />
          ))}
          {!compactCatalog && catalog.length === 0 && (
            <div className="col-span-full">
              <EmptyState
                title="No products with prices"
                description="Add products and a POS list price before ringing sales."
              />
            </div>
          )}
          {compactCatalog &&
            compactRailProducts.length === 0 &&
            catalog.length > 0 && (
              <div className="col-span-full rounded-lg border border-dashed border-[var(--color-border)] px-3 py-4 text-center">
                <p className="m-0 text-sm text-[var(--color-muted)]">
                  {scan.trim().length >= 2
                    ? "No matches — try Browse or a barcode"
                    : "Star favorites in Browse, or sell once to fill recent"}
                </p>
                <Button
                  type="button"
                  size="sm"
                  className="mt-2 min-h-9"
                  disabled={!session.drawer || busy}
                  onClick={() => setBrowseOpen(true)}
                >
                  Browse catalog
                </Button>
              </div>
            )}
          {compactCatalog && catalog.length === 0 && (
            <div className="col-span-full">
              <EmptyState
                title="No products with prices"
                description="Add products and a POS list price before ringing sales."
              />
            </div>
          )}
        </div>

      </section>

      <PosCatalogBrowseSheet
        open={browseOpen}
        onOpenChange={setBrowseOpen}
        catalog={catalog}
        cartQtyByProduct={cartQtyByProduct}
        favoriteIds={favoriteSet}
        drawerOpen={Boolean(session.drawer)}
        busy={busy}
        initialQuery={scan.trim().length >= 2 ? scan : ""}
        onAdd={addProduct}
        onToggleFavorite={onToggleFavorite}
      />

      {/* Cart + checkout scroll as one rail — lines keep natural height; Charge follows (not crushed, not stranded under catalog) */}
      <aside
        className={cn(
          "no-print flex w-full min-h-0 flex-col overflow-y-auto overscroll-contain border-t border-[var(--color-border)] pt-3",
          compactCatalog
            ? "flex-1"
            : "lg:w-[22rem] lg:shrink-0 lg:self-stretch lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0",
        )}
      >
        <div className="flex flex-col pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <span className="text-sm font-semibold tracking-tight">
                Cart
                {cart.length > 0
                  ? ` · ${cart.reduce((n, l) => n + l.qty, 0)}`
                  : ""}
              </span>
              {cart.length > 0 ? (
                <div
                  className={cn(
                    "font-mono font-bold tracking-tight tabular-nums",
                    compactCatalog ? "text-base" : "text-lg",
                  )}
                  aria-label={`Due ₱${moneyDisplay(dueTotal)}`}
                >
                  ₱{moneyDisplay(dueTotal)}
                </div>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              {cart.length > 0 ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="min-h-9 text-[var(--color-muted)]"
                  disabled={busy}
                  onClick={() => void onClearCart()}
                >
                  Clear
                </Button>
              ) : null}
              <Badge
                variant={
                  chip.chip === "offline" || chip.chip === "conflict"
                    ? "destructive"
                    : chip.chip === "pending"
                      ? "secondary"
                      : "success"
                }
                className="text-xs"
                title={chip.label}
              >
                {chip.label}
              </Badge>
            </div>
          </div>
          {(syncPassBusy || (online && pending > 0)) && (
            <ProgressBar
              className="mb-2"
              label={
                syncPassBusy
                  ? pending > 0
                    ? `Syncing queue · ${pending} left…`
                    : "Syncing…"
                  : undefined
              }
            />
          )}

          {syncWarns.length > 0 && (
            <Alert
              variant="muted"
              className={cn(
                "mb-2 space-y-1 text-xs",
                compactCatalog ? "max-h-12 overflow-auto py-1.5" : "py-2",
              )}
            >
              {syncWarns.map((w) => (
                <p key={w.kind} className="m-0">
                  {w.message}
                </p>
              ))}
            </Alert>
          )}

          {conflicts.length > 0 && (
            <Alert
              variant="destructive"
              className={cn(
                "mb-2 space-y-2 text-xs",
                compactCatalog ? "max-h-28 overflow-auto py-1.5" : "py-2",
              )}
            >
              <p className="m-0 text-xs font-semibold">Sync needs attention</p>
              {conflicts.map((c) => (
                <div key={c.id} className="space-y-1.5 text-xs">
                  <p className="m-0 opacity-90">
                    {c.kind.replace("pos.", "")}
                    {c.lastSyncError ? ` — ${c.lastSyncError}` : ""}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {isSoftConfirmableCode(c.conflictCode) && (
                      <Button
                        type="button"
                        size="sm"
                        disabled={conflictBusy || !online}
                        onClick={() => void onConfirmConflict(c)}
                      >
                        Confirm & sync
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={conflictBusy}
                      onClick={() => void onDropConflict(c)}
                    >
                      Drop from queue
                    </Button>
                  </div>
                </div>
              ))}
            </Alert>
          )}

          <ul className="m-0 list-none space-y-2 p-0">
            {cart.map((line) => {
              const cat = catalog.find((c) => c.id === line.productId);
              const onHand = cat?.onHand ?? "0";
              const stockShort =
                cat != null &&
                cat.kind !== "service" &&
                d(line.qty).gt(d(onHand));
              return (
                <li
                  key={line.productId}
                  className={cn(
                    "flex items-center justify-between gap-2 rounded-md px-1.5 py-1 text-sm",
                    stockShort &&
                      "bg-[color-mix(in_oklab,var(--color-destructive)_8%,transparent)]",
                  )}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 truncate font-medium">
                      {stockShort ? (
                        <TriangleAlert
                          className="size-3.5 shrink-0 text-[var(--color-destructive)]"
                          aria-hidden
                        />
                      ) : null}
                      <span className="truncate">{line.name}</span>
                    </div>
                    <div
                      className={cn(
                        "font-mono text-xs",
                        stockShort
                          ? "text-[var(--color-destructive)]"
                          : "text-[var(--color-muted)]",
                      )}
                    >
                      ₱{moneyDisplay(line.unitPrice)}
                      {compactCatalog ? "" : ` × ${line.qty}`}
                      {stockShort
                        ? ` · on hand ${qtyDisplay(onHand)}`
                        : ""}
                    </div>
                    {(line.trackLot || line.trackSerial) && (
                      <div className="mt-1 space-y-1">
                        {line.trackLot && (
                          <Input
                            className="font-mono text-xs"
                            placeholder="Lot #"
                            value={line.lotCode ?? ""}
                            onChange={(e) =>
                              setCartTracking(line.productId, {
                                lotCode: e.target.value,
                              })
                            }
                          />
                        )}
                        {line.trackSerial && (
                          <Input
                            className="font-mono text-xs"
                            placeholder="Serial #"
                            value={line.serialCode ?? ""}
                            onChange={(e) =>
                              setCartTracking(line.productId, {
                                serialCode: e.target.value,
                              })
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(
                        "p-0 font-medium",
                        compactCatalog ? "h-10 w-10 text-base" : "h-7 w-7",
                      )}
                      aria-label={`Decrease ${line.name}`}
                      onClick={() => bumpQty(line.productId, -1)}
                      disabled={line.trackSerial}
                    >
                      −
                    </Button>
                    <span
                      className={cn(
                        "min-w-6 text-center font-mono tabular-nums",
                        compactCatalog ? "text-sm font-semibold" : "text-xs",
                      )}
                      aria-label={`Quantity ${line.qty}`}
                    >
                      {line.qty}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(
                        "p-0 font-medium",
                        compactCatalog ? "h-10 w-10 text-base" : "h-7 w-7",
                      )}
                      aria-label={`Increase ${line.name}`}
                      onClick={() => bumpQty(line.productId, 1)}
                      disabled={line.trackSerial}
                    >
                      +
                    </Button>
                  </div>
                </li>
              );
            })}
            {cart.length === 0 && (
              <li
                className={cn(
                  "rounded-lg border border-dashed border-[var(--color-border)] px-3 text-center text-sm text-[var(--color-muted)]",
                  compactCatalog ? "py-3" : "py-6",
                )}
              >
                Scan or tap a product
              </li>
            )}
          </ul>

          {/* Checkout follows cart lines in the same scroll rail */}
          <div
            className={cn(
              "space-y-2.5 border-t border-[var(--color-border)]",
              compactCatalog ? "mt-2 pt-2" : "mt-3 pt-3",
            )}
          >
            {error && <Alert variant="destructive">{error}</Alert>}
            {lastSale && (
              <Alert
                variant="default"
                className={cn(
                  "text-sm font-medium",
                  compactCatalog ? "py-1.5" : "py-2",
                )}
              >
                {lastSale}
              </Alert>
            )}
            {status && !showOverrideStrip && (
              <Alert
                variant="muted"
                className={cn("text-xs", compactCatalog ? "py-1.5" : "py-2")}
              >
                {status}
              </Alert>
            )}

            <div className="flex min-w-0 items-baseline justify-between gap-2">
              <span className="shrink-0 text-sm text-[var(--color-muted)]">
                Due
              </span>
              <span
                className={cn(
                  "min-w-0 truncate font-mono font-bold tracking-tight tabular-nums",
                  compactCatalog ? "text-lg" : "text-xl",
                )}
              >
                ₱{moneyDisplay(dueTotal)}
              </span>
            </div>
            {discPctNum > 0 && Number.isFinite(discPctNum) && (
              <p className="m-0 text-right text-[10px] text-[var(--color-muted)]">
                {discPctNum}% off · was ₱{moneyDisplay(cartTotal)}
              </p>
            )}

            <div className="block text-xs text-[var(--color-muted)]">
              Pay with
              {compactCatalog ? (
                <div
                  className="mt-1 flex flex-wrap gap-1.5"
                  role="group"
                  aria-label="Pay with"
                >
                  {(online ? session.payMethods : offlineMethods).map((p) => {
                    const active = payMethodId === p.id;
                    return (
                      <Button
                        key={p.id}
                        type="button"
                        size="sm"
                        variant={active ? "default" : "outline"}
                        className="min-h-10 px-3"
                        disabled={
                          !session.drawer || (!online && !p.worksOffline)
                        }
                        onClick={() => {
                          setPayMethodId(p.id);
                          if (
                            !payMethodUsesCashBills({
                              code: p.code,
                              name: p.name,
                            })
                          ) {
                            setTenderPadOpen(false);
                            setTenderAmount("");
                          }
                        }}
                      >
                        {p.name}
                        {!p.worksOffline ? " · net" : ""}
                      </Button>
                    );
                  })}
                </div>
              ) : (
                <Select
                  className="mt-1"
                  value={payMethodId ?? ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    setPayMethodId(id);
                    const p = session.payMethods.find((m) => m.id === id);
                    if (
                      p &&
                      !payMethodUsesCashBills({
                        code: p.code,
                        name: p.name,
                      })
                    ) {
                      setTenderPadOpen(false);
                      setTenderAmount("");
                    }
                  }}
                  disabled={!session.drawer}
                >
                  {(online ? session.payMethods : offlineMethods).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {!p.worksOffline ? " (online)" : ""}
                    </option>
                  ))}
                </Select>
              )}
            </div>

            <div className="block text-xs text-[var(--color-muted)]">
              <div className="flex items-center justify-between gap-2">
                <span>Tender</span>
                {compactCatalog && cashTender && cart.length > 0 ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 px-2 text-xs"
                    disabled={!session.drawer}
                    onClick={() => setTenderPadOpen((o) => !o)}
                  >
                    {tenderPadOpen ? "Hide pad" : "Keypad"}
                  </Button>
                ) : null}
              </div>
              <Input
                className="mt-1 min-h-11 font-mono text-base text-right tabular-nums"
                inputMode={tenderPadOpen ? "none" : "decimal"}
                readOnly={tenderPadOpen}
                placeholder={moneyDisplay(dueTotal)}
                value={tenderAmount}
                onChange={(e) => setTenderAmount(e.target.value)}
                disabled={!session.drawer}
              />
            </div>

            {tenderQuick.length > 0 && cart.length > 0 && (
              <div
                className="flex flex-wrap gap-1.5"
                role="group"
                aria-label="Quick tender"
              >
                {tenderQuick.map((fill) => {
                  const active =
                    tenderAmount.trim() === fill.amount ||
                    (fill.label === "Exact" && !tenderAmount.trim());
                  return (
                    <Button
                      key={fill.label}
                      type="button"
                      size="sm"
                      variant={active ? "default" : "outline"}
                      className={cn(
                        "min-h-9 px-2.5 font-mono text-xs",
                        compactCatalog && "min-h-10",
                      )}
                      disabled={!session.drawer}
                      onClick={() =>
                        setTenderAmount(
                          fill.label === "Exact" ? "" : fill.amount,
                        )
                      }
                    >
                      {fill.label}
                    </Button>
                  );
                })}
              </div>
            )}

            {compactCatalog &&
            cashTender &&
            tenderPadOpen &&
            cart.length > 0 ? (
              <PosTenderKeypad
                value={tenderAmount}
                disabled={!session.drawer}
                onChange={setTenderAmount}
              />
            ) : null}

            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="text-[var(--color-muted)]">Change</span>
              <span
                className={cn(
                  "font-mono font-semibold",
                  changePreview.lt(0) && "text-[var(--color-destructive)]",
                )}
              >
                {changePreview.lt(0)
                  ? `Short ₱${moneyDisplay(changePreview.abs())}`
                  : `₱${moneyDisplay(changePreview)}`}
              </span>
            </div>

            {softLimitNeed === "discount" && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={busy}
                onClick={() => void onRequestDiscountOverride()}
              >
                Request / self-allow override
              </Button>
            )}

            {showOverrideStrip && (
              <div className="space-y-2 rounded-lg border border-[var(--color-border)] px-2.5 py-2">
                {(status?.includes("Waiting") ||
                  status?.includes("Override granted") ||
                  status?.includes("Void approved") ||
                  status?.includes("Void override granted") ||
                  status?.includes("manager PIN") ||
                  status?.includes("OTP")) && (
                  <p className="m-0 text-xs text-[var(--color-muted)]">
                    {status}
                    {status?.includes("Waiting") && online
                      ? " · checking every few seconds"
                      : ""}
                  </p>
                )}
                <p className="m-0 text-xs font-medium text-[var(--color-muted)]">
                  Manager on this register
                  {!online ? " (offline PIN)" : ""}
                </p>
                <div className="flex gap-2">
                  <Input
                    className="min-w-0 flex-1 font-mono"
                    placeholder="User code"
                    autoComplete="off"
                    value={approverCode}
                    onChange={(e) => setApproverCode(e.target.value)}
                    disabled={busy}
                  />
                  <Input
                    className="min-w-0 flex-1 font-mono tracking-widest"
                    type="password"
                    inputMode="numeric"
                    placeholder="PIN"
                    autoComplete="off"
                    value={approverPin}
                    onChange={(e) => setApproverPin(e.target.value)}
                    disabled={busy}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    disabled={
                      busy ||
                      !approverCode.trim() ||
                      approverPin.length < 4
                    }
                    onClick={() => void onApproveWithPin()}
                  >
                    Approve
                  </Button>
                </div>
                <label className="block text-xs text-[var(--color-muted)]">
                  Emergency OTP
                  <div className="mt-1 flex gap-2">
                    <Input
                      className="min-w-0 flex-1 font-mono tracking-widest"
                      inputMode="numeric"
                      placeholder="••••••"
                      maxLength={8}
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value)}
                      disabled={busy || !online}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      disabled={
                        busy || !online || otpCode.trim().length < 4
                      }
                      onClick={() => void onRedeemOtp()}
                    >
                      Use
                    </Button>
                  </div>
                </label>
              </div>
            )}

            <Button
              type="button"
              className="min-h-12 w-full text-base"
              disabled={busy || !session.drawer || cart.length === 0}
              onClick={() => void onSell()}
            >
              {busy ? "Posting…" : "Charge"}
            </Button>

            {receipt && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => printPosReceipt()}
              >
                Print receipt
              </Button>
            )}

            {/* Options / Park / Recall — sheets; never crush cart (plan/35+28) */}
            <div className="flex flex-wrap gap-2 pt-0.5">
              <Button
                type="button"
                variant="outline"
                className="min-h-10 flex-1"
                disabled={!session.drawer}
                onClick={() => setAdvancedOpen(true)}
              >
                {discountPct.trim() || taxPriceMode || taxRatePct.trim()
                  ? "Options · set"
                  : "Options"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="min-h-10 flex-1"
                disabled={busy || !session.drawer || cart.length === 0}
                onClick={() => void onParkCart()}
              >
                Park
              </Button>
              <Button
                type="button"
                variant="outline"
                className="min-h-10 flex-1"
                disabled={busy || !session.drawer}
                onClick={() => {
                  void refreshParkedList().then(() => setParkedOpen(true));
                }}
              >
                Recall
              </Button>
              {recentTickets.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-10 flex-1"
                  onClick={() => setRecentOpen(true)}
                >
                  Recent
                </Button>
              )}
            </div>
          </div>
        </div>
      </aside>

      <PosFloorSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        title="Register"
        description="Switch cashier, close drawer, or exit POS"
      >
        <div className="space-y-2">
          <Button
            type="button"
            variant="outline"
            className="min-h-12 w-full justify-start"
            disabled={busy}
            onClick={() => {
              setMoreOpen(false);
              void onLockToPinLogin("switch");
            }}
          >
            Switch cashier
          </Button>
          {session.drawer ? (
            <Button
              type="button"
              variant="outline"
              className="min-h-12 w-full justify-start"
              disabled={busy || cart.length > 0}
              title={
                cart.length > 0
                  ? "Clear or charge the cart before closing"
                  : undefined
              }
              onClick={() => {
                setMoreOpen(false);
                void onCloseDrawer();
              }}
            >
              Close drawer
              {cart.length > 0 ? " · cart open" : ""}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="outline"
            className="min-h-12 w-full justify-start"
            disabled={busy}
            onClick={() => {
              setMoreOpen(false);
              void onLockToPinLogin("exit");
            }}
          >
            Exit to PIN login
          </Button>
        </div>
      </PosFloorSheet>

      <PosFloorSheet
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
        title="Options"
        description="Discount, tax override, and ticket note"
        trailing={
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            onClick={() => setAdvancedOpen(false)}
          >
            Done
          </Button>
        }
      >
        <PosTicketOptionsFields
          drawerOpen={Boolean(session.drawer)}
          discountSoftCapPct={session.company.discountSoftCapPct}
          orgTaxPriceMode={session.company.taxPriceMode}
          orgTaxRatePct={Number(session.company.taxRatePct)}
          discountPct={discountPct}
          taxPriceMode={taxPriceMode}
          taxRatePct={taxRatePct}
          ticketNote={ticketNote}
          onDiscountPct={setDiscountPct}
          onTaxPriceMode={setTaxPriceMode}
          onTaxRatePct={setTaxRatePct}
          onTicketNote={setTicketNote}
          onDiscountEdited={() => setOverrideRequestId(null)}
        />
      </PosFloorSheet>

      <PosFloorSheet
        open={recentOpen}
        onOpenChange={setRecentOpen}
        title="Recent"
        description="Void or return recent tickets"
      >
        <p className="mb-3 text-xs text-[var(--color-muted)]">
          Void undoes a mistake · Return is for customers coming back
        </p>
        <ul className="m-0 list-none space-y-3 p-0">
          {recentTickets.map((t) => (
            <li
              key={t.id}
              className="space-y-2 border-b border-[var(--color-border)]/80 pb-3 text-sm last:border-0"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-medium">{t.number}</span>
                <span className="font-mono text-sm tabular-nums">
                  ₱{moneyDisplay(t.grossTotal)}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-11 flex-1"
                  disabled={busy}
                  onClick={() => void onVoidTicket(t.id, t.number)}
                >
                  Void
                </Button>
                {t.lifecycle === "posted" ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11 flex-1"
                    disabled={busy}
                    onClick={() => void onReturnTicket(t.id, t.number)}
                  >
                    Return
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  className="min-h-11 w-full"
                  disabled={busy || !online}
                  onClick={() => void onRequestVoidOverride(t.id, t.number)}
                >
                  Request void override
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </PosFloorSheet>

      <PosFloorSheet
        open={parkedOpen}
        onOpenChange={setParkedOpen}
        title="Parked"
        description="Recall a suspended ticket — same register offline; others when online"
      >
        {parkedTickets.length === 0 ? (
          <p className="m-0 py-8 text-center text-sm text-[var(--color-muted)]">
            No parked tickets
          </p>
        ) : (
          <ul className="m-0 list-none space-y-3 p-0">
            {parkedTickets.map((p) => (
              <li
                key={p.id}
                className="space-y-2 border-b border-[var(--color-border)]/80 pb-3 last:border-0"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{p.number}</div>
                    <div className="text-xs text-[var(--color-muted)]">
                      {p.terminalName} · {p.itemCount} items
                    </div>
                  </div>
                  <span className="shrink-0 font-mono text-sm tabular-nums">
                    ₱{moneyDisplay(p.grossTotal)}
                  </span>
                </div>
                <Button
                  type="button"
                  className="min-h-11 w-full"
                  disabled={busy}
                  onClick={() => void onRequestRecall(p)}
                >
                  Recall
                </Button>
              </li>
            ))}
          </ul>
        )}
      </PosFloorSheet>

      <Dialog
        open={parkConflict != null}
        onOpenChange={(open) => {
          if (!open) setParkConflict(null);
        }}
      >
        <DialogContent className="space-y-4">
          <div>
            <DialogTitle>Which cart?</DialogTitle>
            <DialogDescription className="mt-1 text-sm text-[var(--color-muted)]">
              Pick one — the other stays as-is (no merge).
            </DialogDescription>
          </div>
          <div className="space-y-2">
            {parkConflict?.options.map((opt) => (
              <Button
                key={opt.key}
                type="button"
                variant={opt.key === "current" ? "outline" : "default"}
                className="min-h-14 w-full flex-col items-start gap-0.5 py-2"
                disabled={busy}
                onClick={() => {
                  if (opt.key === "current") {
                    setParkConflict(null);
                    setParkedOpen(false);
                    return;
                  }
                  if (!parkConflict) return;
                  void loadParkIntoCart(
                    parkConflict.incomingId,
                    parkConflict.incomingLocal,
                  );
                }}
              >
                <span className="text-sm font-semibold">{opt.title}</span>
                <span className="text-xs font-normal opacity-80">
                  {opt.itemCount} items · ₱{opt.dueAmount}
                </span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
